//////////////////
// IMPORTS
//////////////////

// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import Radium from "radium";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
import ReduxState from "../../declarations/state.d";

// meeting-relief-goals imports
import IGoalsWrapper from "./goals-wrapper.d";
import { BaseGoalComponent } from "./base-goal.component";
import * as styles from "./goals-wrapper.styles";

// other internals
import { logger } from "@util/logger.util";
import { removeGoal, upsertMeetingRelief } from "@mutations";
import { standardGoals } from "@util/goal.util";
import { snackBarOpen } from "@actions/snack-bar.actions";

// util
import { track, TrackingEvents } from "@clockwise/web-commons/src/util/analytics.util";

// material-ui
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
} from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";

//////////////////
// COMPONENT
//////////////////
@Radium
export class MeetingReliefGoalComponent extends BaseGoalComponent<IGoalsWrapper.IMeetingRelief.State> {
  constructor(props: IGoalsWrapper.Props) {
    super(props, standardGoals.MeetingRelief);

    this.state = this.calculateStateFromProps();
  }

  private calculateStateFromProps = (): IGoalsWrapper.IMeetingRelief.State => {
    const goal = this.findGoal();
    const goalOptions = goal && goal.options;

    const meetingReliefOptions =
      goalOptions && (goalOptions.specificOptions as ISchema.IGoalMeetingReliefOptions);

    return {
      selected: !!goal,
      loading: false,
      reliefThreshold:
        (meetingReliefOptions && meetingReliefOptions.fatigueThresholdMinutes) || 120,
      reliefMinutes: (meetingReliefOptions && meetingReliefOptions.meetingReliefBreakMinutes) || 5,
    };
  };

  // Handlers

  private onUpdatePreferencesSuccess = () => {
    this.props.snackBarOpen("Updated preferences");
  };

  private onMutateOnToggleSuccess = (isInputChecked: boolean) => {
    this.setState({ loading: false });
    this.props.snackBarOpen(
      `${isInputChecked ? "Added" : "Removed"} "Smart Meeting Breaks" preference`,
    );
  };

  protected mutateOnToggle(isInputChecked: boolean): void {
    const {
      featureGrid: { meetingReliefSettingVisibility },
      org,
    } = this.props;

    track(TrackingEvents.SETTINGS.UPDATE_MEETING_RELIEF, {
      enabled: isInputChecked,
      meetingReliefSettingVisibility,
    });

    if (isInputChecked) {
      this.setState({ loading: true });
      upsertMeetingRelief(
        this.props.relay.environment,
        {
          orgId: org.id,
          options: {
            fatigueThresholdMinutes: this.state.reliefThreshold,
            meetingReliefBreakMinutes: this.state.reliefMinutes,
          },
        },
        this.onMutateOnToggleSuccess.bind(this, isInputChecked),
        this.onMutationFailure,
      );
    } else {
      const goal = this.findGoalInput();
      if (goal) {
        this.setState({ loading: true });
        removeGoal(
          this.props.relay.environment,
          { goal, orgId: org.id },
          this.onMutateOnToggleSuccess.bind(this, isInputChecked),
          this.onMutationFailure,
        );
      } else {
        logger.debug("Couldn't find goal to remove");
      }
    }
  }

  private onMeetingReliefThresholdSet = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const reliefThreshold = parseInt(e.target.value);
    this.setState({ reliefThreshold }, this.callUpsertMeetingRelief);

    const {
      featureGrid: { meetingReliefSettingVisibility },
    } = this.props;

    track(TrackingEvents.SETTINGS.UPDATE_MEETING_RELIEF_THRESHOLD, {
      thresholdMinutes: reliefThreshold,
      durationMinutes: this.state.reliefMinutes,
      meetingReliefSettingVisibility,
    });
  };

  private onMeetingReliefMinutesSet = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const reliefMinutes = parseInt(e.target.value);
    this.setState({ reliefMinutes }, this.callUpsertMeetingRelief);

    const {
      featureGrid: { meetingReliefSettingVisibility },
    } = this.props;

    track(TrackingEvents.SETTINGS.UPDATE_MEETING_RELIEF_DURATION, {
      thresholdMinutes: this.state.reliefThreshold,
      durationMinutes: reliefMinutes,
      meetingReliefSettingVisibility,
    });
  };

  private callUpsertMeetingRelief = () => {
    const org = this.props.org;
    const options = {
      fatigueThresholdMinutes: this.state.reliefThreshold,
      meetingReliefBreakMinutes: this.state.reliefMinutes,
    };

    upsertMeetingRelief(
      this.props.relay.environment,
      { options, orgId: org.id },
      this.onUpdatePreferencesSuccess,
      this.onMutationFailure,
    );
  };

  private onMutationFailure = (): void => {
    this.setState({ loading: false });
    this.props.snackBarOpen("An error occurred!");
  };

  private formatLabel = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} minutes`;
    }

    return `${minutes / 60} hours`;
  };

  private renderMeetingReliefOptions = (): JSX.Element => {
    const thresholds = [30, 60, 90, 120, 150, 180, 210, 240].map((minutes, index) => (
      <MenuItem key={index} value={minutes}>
        {this.formatLabel(minutes)}
      </MenuItem>
    ));

    const minutes = [5, 15, 30].map((minutes, index) => (
      <MenuItem key={index} value={minutes}>
        {this.formatLabel(minutes)}
      </MenuItem>
    ));

    return (
      <div style={styles.container}>
        <FormControl style={styles.meetingReliefThresholdForm}>
          <InputLabel
            style={{ width: 344 }}
            shrink
            htmlFor="cw-meeting-relief-consecutive-duration-select"
          >
            When in consecutive meetings for longer than
          </InputLabel>
          <Select
            value={this.state.reliefThreshold}
            onChange={this.onMeetingReliefThresholdSet}
            name="When in consecutive meetings for longer than"
            disabled={!this.state.selected || this.state.loading || this.props.disabled}
            inputProps={{
              name: "When in consecutive meetings for longer than",
              id: "cw-meeting-relief-consecutive-duration-select",
            }}
          >
            {thresholds}
          </Select>
        </FormControl>
        <FormControl style={styles.meetingReliefMinutesForm}>
          <InputLabel
            style={{ width: 244 }}
            shrink
            htmlFor="cw-meeting-relief-relief-duration-select"
          >
            Create breaks of
          </InputLabel>
          <Select
            value={this.state.reliefMinutes}
            onChange={this.onMeetingReliefMinutesSet}
            name="Create breaks of"
            disabled={!this.state.selected || this.state.loading || this.props.disabled}
            inputProps={{
              name: "Create breaks of",
              id: "cw-meeting-relief-relief-duration-select",
            }}
          >
            {minutes}
          </Select>
        </FormControl>
      </div>
    );
  };

  public render(): JSX.Element {
    return (
      <div style={styles.goal}>
        <FormControl>
          <FormControlLabel
            control={
              <Switch color="primary" checked={this.state.selected && !this.props.disabled} />
            }
            onChange={this.onToggle}
            disabled={this.state.loading || this.props.disabled}
            label="When possible, create a break between consecutive meetings"
            labelPlacement="end"
          />
        </FormControl>
        {this.state.selected && !this.props.disabled && this.renderMeetingReliefOptions()}
      </div>
    );
  }
}

//////////////////
// WITH STYLES
//////////////////
export const MeetingReliefGoalStyled = withStyles(styles.classes)(MeetingReliefGoalComponent);

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: ReduxState.Redux, _ownProps: IGoalsWrapper.Container) {
  return {
    featureGrid: _state.featureGrid,
  };
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
})(MeetingReliefGoalStyled);

//////////////////
// RELAY
//////////////////
import { fragments } from "./MeetingReliefGoal.gql";
export const MeetingReliefGoal = createFragmentContainer(reduxContainer, fragments);
